// src/pods/podStageComposition.ts
var ROLE_SPEED = {
  ps08x: 0.8,
  ps: 1,
  ps15x: 1.5,
  ps2x: 2,
  trans: 1
};
var isTargetRole = (role) => role !== "trans";
var _warnedTrailingKnownStages = /* @__PURE__ */ new Set();
function buildMainStage(sentence, stage, sentenceIdx, playlist, uniformSpeed) {
  const useUniform = typeof uniformSpeed === "number" && Number.isFinite(uniformSpeed) && uniformSpeed > 0;
  const speedFor = (role) => useUniform ? uniformSpeed : ROLE_SPEED[role] ?? 1;
  const sentencePlays = [];
  for (let j = 0; j < playlist.length; j++) {
    let playRole = playlist[j];
    if (playRole === "explainer") {
      if (playlist.includes("trans")) continue;
      playRole = "trans";
    }
    if (playRole === "trans" && !sentence.known_audio_id) continue;
    const isTrans = playRole === "trans";
    const audioId = isTrans ? sentence.known_audio_id : sentence.target_audio_id;
    sentencePlays.push({
      sentenceIdx,
      stage,
      playRole,
      audioId,
      text: isTrans ? sentence.known_text : sentence.target_text,
      playbackSpeed: speedFor(playRole),
      speedIsFinal: useUniform,
      glueToNextChunk: false
      // set on the ACTUAL last play below
    });
  }
  if (sentencePlays.length === 0) return [];
  const last = sentencePlays[sentencePlays.length - 1];
  if (!isTargetRole(last.playRole)) {
    const lastTarget = [...sentencePlays].reverse().find((p) => isTargetRole(p.playRole));
    const closeRole = lastTarget?.playRole ?? "ps";
    sentencePlays.push({
      sentenceIdx,
      stage,
      playRole: closeRole,
      audioId: sentence.target_audio_id,
      text: sentence.target_text,
      playbackSpeed: speedFor(closeRole),
      speedIsFinal: useUniform,
      glueToNextChunk: false
    });
    if (!_warnedTrailingKnownStages.has(stage)) {
      _warnedTrailingKnownStages.add(stage);
      console.warn(
        `[podScheduler] Stage ${stage} playlist [${playlist.join(", ")}] ends on a non-target slot \u2014 learner would be stranded on the known language. Appended a target rep as a safety net; fix the saved stagePlaylist so it ends on a ps* slot.`
      );
    }
  }
  sentencePlays[sentencePlays.length - 1].glueToNextChunk = !!sentence.glue_to_next;
  return sentencePlays;
}

// src/pods/fusionDrill.ts
var DRILL_SLICE_PLAYBACK_ENABLED = false;
var VERIFIED_DRILL_CUTS_ENABLED = true;
var ALL_STRIPS = -2;
var normalizeForAudio = (t) => (t || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[.!。！]+$/, "");
var normalizeForLookup = (t) => (t || "").normalize("NFKC").toLowerCase().replace(/[^\p{L}\p{M}\p{N}]+/gu, " ").trim();
var lookupKeyVariants = (t) => {
  const base = (t || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[\u2018\u2019]/g, "'");
  if (!base) return [];
  const bare = base.replace(/[.!?…。！？؟¿¡\s]+$/u, "");
  return Array.from(/* @__PURE__ */ new Set([base, bare, `${bare}.`, `${bare}?`, `${bare}!`]));
};
var voiceFamily = (id) => (id || "").replace(/^(xai|azure|cartesia)_/, "");
var POD_KNOWN_VOICE_FAMILY = "gfzdpspr5fdp";
var POD_KNOWN_FEMALE_VOICE_FAMILY = "bedd6226";
var otherPodVoice = (voiceId) => {
  const v = voiceFamily(voiceId);
  if (v === POD_KNOWN_VOICE_FAMILY) return POD_KNOWN_FEMALE_VOICE_FAMILY;
  if (v === POD_KNOWN_FEMALE_VOICE_FAMILY) return POD_KNOWN_VOICE_FAMILY;
  return null;
};
function resolveKnownWithFallback(voice, inVoice) {
  const own = inVoice(voice);
  if (own) return own;
  if (own === void 0) return null;
  const other = otherPodVoice(voice);
  return other && inVoice(other) || null;
}
function knownClipsForText(text, lookup) {
  const whole = text ? lookup(text) : null;
  if (whole) return [whole];
  const parts = (text || "").split(/(?<=[.!?])\s+/).map((t) => t.trim()).filter(Boolean);
  if (parts.length < 2) return [];
  const ids = parts.map((t) => lookup(t));
  return ids.every(Boolean) ? ids : [];
}
var fineKnownKey = (voiceId, normText) => `${voiceFamily(voiceId)}|${normText}`;
var dominantVoiceFamily = (voiceIds) => {
  const n = /* @__PURE__ */ new Map();
  for (const v of voiceIds) if (v) n.set(voiceFamily(v), (n.get(voiceFamily(v)) || 0) + 1);
  let best = null;
  for (const [k, c] of n) if (c > (best ? n.get(best) : 0)) best = k;
  return best;
};
var SENTENCE_PUNCT = /[.!?…。！？؟]/;
function fuseSpans(spans, mode) {
  if (spans.length <= 1) return spans;
  const out = [];
  if (mode === "chained") {
    for (let i = 0; i < spans.length - 1; i++) out.push({ start: spans[i].start, end: spans[i + 1].end });
  } else {
    for (let i = 0; i < spans.length; i += 2)
      out.push({ start: spans[i].start, end: spans[Math.min(i + 1, spans.length - 1)].end });
  }
  return out;
}
function spanLadder(n, mode) {
  let spans = Array.from({ length: n }, (_, i) => ({ start: i, end: i }));
  const levels = [spans];
  while (spans.length > 1) {
    spans = fuseSpans(spans, mode);
    levels.push(spans);
  }
  return levels;
}
function partitionUnits(turnText, units) {
  const text = turnText || "";
  const lower = text.toLowerCase();
  const groups = [[]];
  let cursor = 0;
  for (let i = 0; i < units.length; i++) {
    const surface = (units[i].target_surface || "").toLowerCase();
    const idx = surface ? lower.indexOf(surface, cursor) : -1;
    if (i > 0 && idx !== -1 && SENTENCE_PUNCT.test(text.slice(cursor, idx))) groups.push([]);
    groups[groups.length - 1].push(i);
    if (idx !== -1) cursor = idx + surface.length;
  }
  return groups.filter((g) => g.length > 0);
}
var wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
var isBareInterjection = (unit) => wordCount(unit?.target_surface) <= 1;
function glueLeadingInterjection(groups, units) {
  if (groups.length >= 2 && groups[0].length === 1 && (!units || isBareInterjection(units[groups[0][0]]))) {
    return [[...groups[0], ...groups[1]], ...groups.slice(2)];
  }
  return groups;
}
function mapMatchesText(turnText, units) {
  const text = ` ${normalizeForLookup(turnText)} `;
  return units.every((u) => text.includes(` ${normalizeForLookup(u.target_surface)} `));
}
function buildFusionGroups(input, opts) {
  const units = (input.fineMap || []).filter(
    (u) => u && (u.kind === void 0 || u.kind === "atom" || u.kind === "passthrough") && u.target_surface
  );
  if (units.length === 0) return null;
  if (!mapMatchesText(input.turnTargetText, units)) return null;
  const raw = partitionUnits(input.turnTargetText, units);
  const glued = glueLeadingInterjection(raw, units);
  const rows = input.rows || [];
  const singleRow = rows.length === 1;
  if (!singleRow && raw.length !== rows.length) return null;
  const takeg = input.takegAudioIds;
  if (takeg && takeg.length !== glued.length) return null;
  const gluedOffset = glued.length === raw.length ? 0 : 1;
  const glueHasNoTake = gluedOffset === 1 && !singleRow && !(takeg && takeg[0]);
  const splitGlued = (!!opts?.splitGlued || glueHasNoTake) && gluedOffset === 1;
  const groupsSrc = splitGlued ? raw : glued;
  const takegIndexFor = (gi) => splitGlued ? Math.max(0, gi - 1) : gi;
  const flatStarts = [];
  {
    let off = 0;
    for (const g of groupsSrc) {
      flatStarts.push(off);
      off += g.length;
    }
  }
  const windowIndex = {};
  for (const w of input.windowKnownMap || []) windowIndex[`${w.start}-${w.end}`] = w.known;
  const groups = groupsSrc.map((unitIdxs, gi) => {
    const groupUnits = unitIdxs.map((i) => units[i]);
    const rowFirst = singleRow ? 0 : splitGlued ? gi : gi === 0 ? 0 : gi + gluedOffset;
    const rowLast = singleRow ? 0 : splitGlued ? gi : gi === 0 ? gluedOffset : gi + gluedOffset;
    const rowsOfGroup = rows.slice(rowFirst, rowLast + 1);
    const perSentenceRow = !singleRow && rowsOfGroup.length === 1 ? rowsOfGroup[0] : null;
    const wholeTurnGroup = singleRow && groupsSrc.length === 1 ? rows[0] : null;
    const coversTurn = groupsSrc.length === 1;
    const takegId = takeg ? takeg[takegIndexFor(gi)] || null : null;
    const sliceable = DRILL_SLICE_PLAYBACK_ENABLED;
    return {
      groupIndex: gi,
      units: groupUnits,
      windowKnowns: windowIndex,
      flatStart: flatStarts[gi],
      takegAudioId: takegId,
      wholeTargetClipId: perSentenceRow?.targetAudioId || wholeTurnGroup?.targetAudioId || takegId || (coversTurn ? input.turnTargetAudioId || null : null),
      wholeKnownClipId: perSentenceRow?.knownAudioId || wholeTurnGroup?.knownAudioId || (coversTurn ? input.turnKnownAudioId || null : null),
      wholeKnownClipIds: rowsOfGroup.length > 1 && rowsOfGroup.every((r) => r.knownAudioId) ? rowsOfGroup.map((r) => r.knownAudioId) : [],
      targetText: perSentenceRow?.targetText || wholeTurnGroup?.targetText || (rowsOfGroup.length > 1 ? rowsOfGroup.map((r) => r.targetText).filter(Boolean).join(" ") : groupUnits.map((u) => u.target_surface).join(" ")),
      knownText: perSentenceRow?.knownText || wholeTurnGroup?.knownText || (rowsOfGroup.length > 1 ? rowsOfGroup.map((r) => r.knownText).filter(Boolean).join(" ") : groupUnits.map((u) => u.gloss || "").filter(Boolean).join(" ")),
      rowFirst,
      rowLast,
      sliceable
    };
  });
  if (groups.some((g) => !g.wholeTargetClipId)) return null;
  return groups;
}
function verifiedCutGroups(sentence, rowIndex, cut) {
  if (!cut || !sentence.targetAudioId) return null;
  if (cut.clip !== sentence.targetAudioId || !Array.isArray(cut.lines) || cut.lines.length === 0) return null;
  const lines = cut.lines;
  const cuts = lines.length > 1 || (lines[0].chunks || []).length >= 2;
  if (!cuts) return null;
  const ok = (a, b) => Number.isFinite(a) && Number.isFinite(b) && a < b;
  let at = 0;
  for (const l of lines) {
    if (!l?.t || !ok(l.t[1], l.t[2]) || l.t[1] < at) return null;
    for (const c of l.chunks || []) if (!ok(c[1], c[2]) || c[1] < l.t[1] - 1 || c[2] > l.t[2] + 1) return null;
    at = l.t[2];
  }
  const multi = lines.length > 1;
  const knownClipLive = !!cut.knownClip && cut.knownClip === sentence.knownAudioId;
  return lines.map((l, li) => {
    const chunks = (l.chunks || []).length >= 2 ? l.chunks : [];
    const kText = multi ? l.k?.[0] || "" : sentence.knownText;
    const kSpan = multi && knownClipLive && l.k && l.k.length === 3 && ok(l.k[1], l.k[2]) ? { startMs: l.k[1], endMs: l.k[2] } : null;
    const units = chunks.length ? chunks.map(([text, startMs, endMs, gloss, ks, ke, standalone]) => ({
      kind: "atom",
      target_surface: text,
      gloss: gloss || void 0,
      target_start_ms: startMs,
      target_end_ms: endMs,
      // the chunk's English span is used only when cut from the English clip the card plays now
      // the chunk's own standalone known clip wins over the cut (job #707: the cut was scored too tight)
      ...typeof standalone === "string" && standalone ? { known_clip: { id: standalone } } : knownClipLive && ks != null && ke != null && ok(ks, ke) ? { known_clip: { id: cut.knownClip, startMs: ks, endMs: ke } } : {}
    })) : [{ kind: "atom", target_surface: l.t[0], gloss: kText || void 0, target_start_ms: l.t[1], target_end_ms: l.t[2] }];
    const g = {
      groupIndex: li,
      units,
      windowKnowns: {},
      flatStart: 0,
      takegAudioId: sentence.targetAudioId,
      wholeTargetClipId: sentence.targetAudioId,
      wholeKnownClipId: multi ? kSpan ? cut.knownClip : null : sentence.knownAudioId,
      wholeKnownClipIds: [],
      targetText: multi ? l.t[0] : sentence.targetText,
      knownText: kText,
      rowFirst: rowIndex,
      rowLast: rowIndex,
      sliceable: chunks.length >= 2,
      verifiedCuts: true
    };
    if (multi) {
      g.wholeTargetSpan = { startMs: l.t[1], endMs: l.t[2] };
      if (kSpan) g.wholeKnownSpan = kSpan;
      g.line = {
        index: li,
        count: lines.length,
        turnClipId: sentence.targetAudioId,
        turnText: sentence.targetText,
        turnKnownClipId: sentence.knownAudioId,
        turnKnownText: sentence.knownText
      };
    }
    return g;
  });
}
function composeDrillCard(groups, rung, mode, lookupKnownClip) {
  const strips = [];
  const steps = [];
  for (const g of groups) {
    const r = rungStepsForGroup(g, rung, mode, lookupKnownClip, strips.length);
    strips.push(...r.strips);
    steps.push(...r.steps);
  }
  const line = groups[0]?.line;
  if (line && line.count > 1 && groups.length === line.count) {
    const turn = { kind: "chunk", stripIndex: ALL_STRIPS, text: line.turnText, clip: { id: line.turnClipId }, whole: true };
    if (line.turnKnownClipId) {
      steps.push(
        turn,
        { kind: "known", stripIndex: ALL_STRIPS, text: line.turnKnownText || "", clip: { id: line.turnKnownClipId }, of: line.turnText, whole: true },
        { ...turn },
        { ...turn }
      );
    } else steps.push(turn);
  }
  return { strips, steps };
}
function lineKnownFor(group, lookupKnownClip) {
  const sentText = group.knownText;
  const glossJoin = group.units.map((u) => u.gloss || "").filter(Boolean).join(" ");
  const knownText = sentText || glossJoin;
  const ownSlice = group.wholeKnownSpan && group.wholeKnownClipId ? { id: group.wholeKnownClipId, ...group.wholeKnownSpan } : null;
  if (ownSlice) return { knownText, knownClips: [ownSlice] };
  const clipId = (sentText ? lookupKnownClip(sentText) : null) || (group.wholeKnownSpan ? null : group.wholeKnownClipId) || (glossJoin ? lookupKnownClip(glossJoin) : null);
  const ids = clipId ? [clipId] : group.wholeKnownClipIds.length ? group.wholeKnownClipIds : knownClipsForText(knownText, lookupKnownClip);
  return { knownText, knownClips: ids.map((id) => ({ id })) };
}
var SKIP_FINEST_RUNG = false;
function ladderFor(group, mode) {
  if (!group.sliceable) return [[{ start: 0, end: group.units.length - 1 }]];
  if (group.verifiedCuts) {
    return [group.units.map((_, i) => ({ start: i, end: i })), [{ start: 0, end: group.units.length - 1 }]];
  }
  let levels = spanLadder(group.units.length, mode);
  return levels;
}
function groupDepth(group, mode) {
  return ladderFor(group, mode).length;
}
function dropKnownsForHeardPhrases(steps, heard) {
  const chunkText = /* @__PURE__ */ new Map();
  for (const st of steps) if (st.kind === "chunk" && !chunkText.has(st.stripIndex)) chunkText.set(st.stripIndex, st.text);
  return steps.filter(
    (st) => st.kind !== "known" || st.whole || !heard.has(normalizeForAudio(st.of ?? chunkText.get(st.stripIndex)))
  );
}
function dropChunksWithoutKnown(steps) {
  const key = (st, text) => `${st.stripIndex}|${st.piece ?? ""}|${text ?? ""}`;
  const voiced = /* @__PURE__ */ new Set();
  for (const st of steps) if (st.kind === "known" && !st.whole && st.clip) voiced.add(key(st, st.of));
  return steps.filter((st) => st.kind !== "chunk" || st.whole || voiced.has(key(st, st.text)));
}
function heardTargetKeysFromExposures(sentences, mode, maxRungs = 8) {
  const out = /* @__PURE__ */ new Set();
  for (const s of sentences) {
    if (!(s.exposures > 0)) continue;
    for (let r = 0; r < Math.min(s.exposures, maxRungs); r++) {
      for (const g of s.groups || []) {
        for (const st of rungStepsForGroup(g, r, mode, () => null).steps) {
          if (st.kind === "chunk" && !st.whole) out.add(normalizeForAudio(st.text));
        }
      }
    }
  }
  return out;
}
var FUSION_TIER_PREFIX = "fusion-r";
function buildFusionRungPlays(sentence, group, rung, sentenceIdx, mode, lookupKnownClip) {
  const { steps } = rungStepsForGroup(group, rung, mode, lookupKnownClip);
  const plays = [];
  for (const st of steps) {
    if (!st.clip) continue;
    plays.push({
      sentenceIdx,
      stage: 0,
      tier: `${FUSION_TIER_PREFIX}${rung}`,
      playRole: st.kind === "known" ? "trans" : "ps",
      audioId: st.clip.id,
      text: st.text,
      playbackSpeed: 1,
      glueToNextChunk: false,
      ...st.clip.startMs != null && st.clip.endMs != null ? { startMs: st.clip.startMs, endMs: st.clip.endMs } : {}
    });
  }
  if (plays.length > 0 && sentence.glue_to_next) plays[plays.length - 1].glueToNextChunk = true;
  return plays;
}
function rungStepsForGroup(group, rung, mode, lookupKnownClip, stripOffset = 0) {
  const ladder = ladderFor(group, mode);
  const level = ladder[Math.min(rung, ladder.length - 1)];
  const strips = [];
  const steps = [];
  const pushTktt = (stripIndex, chunkText, chunkClip, knownText, knownClips, whole = false, piece, lineKnown = false) => {
    const w = { ...whole ? { whole: true } : {}, ...piece != null ? { piece } : {} };
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip, ...w });
    for (const clip of knownClips) steps.push({ kind: "known", stripIndex, text: knownText, clip, of: chunkText, ...w, ...lineKnown ? { lineKnown: true } : {} });
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip, ...w });
    steps.push({ kind: "chunk", stripIndex, text: chunkText, clip: chunkClip, ...w });
  };
  if (level.length === 1) {
    const { knownText, knownClips } = lineKnownFor(group, lookupKnownClip);
    strips.push({ target: group.targetText, known: knownText });
    pushTktt(
      stripOffset,
      group.targetText,
      group.wholeTargetClipId ? { id: group.wholeTargetClipId, ...group.wholeTargetSpan || {} } : null,
      knownText,
      knownClips,
      true
    );
    return { strips, steps };
  }
  const pieceTexts = [];
  const pieceKnowns = [];
  level.forEach((span, si) => {
    const us = group.units.slice(span.start, span.end + 1);
    const chunkText = us.map((u) => u.target_surface).join(" ");
    const chunkClip = {
      id: group.takegAudioId,
      startMs: us[0].target_start_ms,
      endMs: us[us.length - 1].target_end_ms
    };
    const knownText = us.length === 1 ? us[0].gloss || "" : group.windowKnowns[`${group.flatStart + span.start}-${group.flatStart + span.end}`] || us.map((u) => u.gloss || "").filter(Boolean).join(" ");
    const ownKnown = group.verifiedCuts && us.length === 1 ? us[0].known_clip : void 0;
    const knownId = ownKnown ? null : knownText ? lookupKnownClip(knownText) : null;
    let pieceKnownClips = ownKnown ? [{ ...ownKnown }] : knownId ? [{ id: knownId }] : [];
    let pieceKnownText = knownText;
    let lineKnown = false;
    if (!pieceKnownClips.length) {
      const line = lineKnownFor(group, lookupKnownClip);
      pieceKnownClips = line.knownClips;
      if (pieceKnownClips.length) {
        pieceKnownText = line.knownText;
        lineKnown = true;
      }
    }
    if (group.verifiedCuts) {
      pieceTexts.push(chunkText);
      pieceKnowns.push(knownText);
      pushTktt(stripOffset, chunkText, chunkClip, pieceKnownText, pieceKnownClips, false, si, lineKnown);
    } else {
      strips.push({ target: chunkText, known: knownText });
      pushTktt(stripOffset + si, chunkText, chunkClip, pieceKnownText, pieceKnownClips, false, void 0, lineKnown);
    }
  });
  if (group.verifiedCuts) {
    strips.push({ target: group.targetText, known: group.knownText, pieces: pieceTexts, pieceKnowns });
    const whole = rungStepsForGroup(group, ladder.length - 1, mode, lookupKnownClip, stripOffset);
    steps.push(...whole.steps);
  }
  return { strips, steps };
}

// src/pods/podTurns.ts
function computeTurnSpans(sentences) {
  const spans = [];
  let start = 0;
  for (let i = 0; i < sentences.length; i++) {
    if (!sentences[i].glue_to_next || i === sentences.length - 1) {
      spans.push({ start, end: i });
      start = i + 1;
    }
  }
  return spans;
}
function turnSpanForIndex(spans, index) {
  return spans.find((s) => index >= s.start && index <= s.end) ?? null;
}
function podPlayShowsTurnText(play) {
  return !!play && !play.isLayer1;
}
function podLapPlayedSentenceIndices(plays) {
  const set = /* @__PURE__ */ new Set();
  for (const p of plays) {
    if (podPlayShowsTurnText(p)) set.add(p.sentenceIdx - 1);
  }
  return Array.from(set).sort((a, b) => a - b);
}
function podLapDisplayRange(totalSentences2, playedIndices) {
  if (totalSentences2 <= 0 || playedIndices.length === 0) return null;
  return {
    start: Math.max(0, Math.min(...playedIndices)),
    end: Math.min(totalSentences2 - 1, Math.max(...playedIndices))
  };
}

// src/pods/podCohorts.ts
var POD_COLD_START_MIN_SENTENCES = 2;
var sameScene = (a, b) => a == null || b == null || a === b;
function buildPodExchangeCohorts(rows) {
  const sceneRuns = [];
  let start = 0;
  let scene = null;
  for (let i = 0; i < rows.length; i++) {
    const sc = rows[i].scene_number ?? null;
    if (i > start && !sameScene(scene, sc)) {
      sceneRuns.push({ start, end: i });
      start = i;
    }
    if (sc != null) scene = sc;
    else if (i === start) scene = null;
  }
  if (rows.length > start) sceneRuns.push({ start, end: rows.length });
  const cohorts = [];
  for (const run of sceneRuns) {
    const turnStarts = [];
    let turnStart = run.start;
    for (let i = run.start; i < run.end; i++) {
      if (!rows[i].glue_to_next || i === run.end - 1) {
        turnStarts.push(turnStart);
        turnStart = i + 1;
      }
    }
    for (let t = 0; t < turnStarts.length; t += 2) {
      const exStart = turnStarts[t];
      const exEnd = t + 2 < turnStarts.length ? turnStarts[t + 2] : run.end;
      cohorts.push({ start: exStart, size: exEnd - exStart });
    }
  }
  return cohorts;
}
function applyPodColdStartWindow(cohorts) {
  let size = 0;
  let n = 0;
  while (n < cohorts.length && size < POD_COLD_START_MIN_SENTENCES) {
    size += cohorts[n].size;
    n++;
  }
  return n > 1 ? [{ start: 0, size }, ...cohorts.slice(n)] : [...cohorts];
}
function buildPodSentenceCohorts(rows) {
  return rows.map((_, i) => ({ start: i, size: 1 }));
}
function buildPodCohorts(rows) {
  return applyPodColdStartWindow(buildPodSentenceCohorts(rows));
}
function podCohortOrdinalForIndex(cohorts, idx) {
  return cohorts.findIndex((c) => idx >= c.start && idx < c.start + c.size);
}
var totalSentences = (cohorts) => {
  const last = cohorts[cohorts.length - 1];
  return last ? last.start + last.size : 0;
};
function podCohortRoundFor(cohorts, stored) {
  const started = cohorts.filter((c) => c.start < stored).length;
  return started + 1 + Math.max(0, stored - totalSentences(cohorts));
}
function podRatchetAfterLap(cohorts, stored) {
  if (cohorts.length === 0) return stored + 1;
  const started = cohorts.filter((c) => c.start < stored).length;
  const completing = cohorts[Math.min(started, cohorts.length - 1)];
  return Math.max(completing.start + completing.size, stored + 1);
}

export { ALL_STRIPS, FUSION_TIER_PREFIX, POD_COLD_START_MIN_SENTENCES, POD_KNOWN_FEMALE_VOICE_FAMILY, POD_KNOWN_VOICE_FAMILY, ROLE_SPEED, SENTENCE_PUNCT, SKIP_FINEST_RUNG, VERIFIED_DRILL_CUTS_ENABLED, applyPodColdStartWindow, buildFusionGroups, buildFusionRungPlays, buildMainStage, buildPodCohorts, buildPodExchangeCohorts, buildPodSentenceCohorts, composeDrillCard, computeTurnSpans, dominantVoiceFamily, dropChunksWithoutKnown, dropKnownsForHeardPhrases, fineKnownKey, fuseSpans, glueLeadingInterjection, groupDepth, heardTargetKeysFromExposures, isTargetRole, knownClipsForText, lookupKeyVariants, mapMatchesText, normalizeForAudio, normalizeForLookup, otherPodVoice, partitionUnits, podCohortOrdinalForIndex, podCohortRoundFor, podLapDisplayRange, podLapPlayedSentenceIndices, podPlayShowsTurnText, podRatchetAfterLap, resolveKnownWithFallback, rungStepsForGroup, spanLadder, turnSpanForIndex, verifiedCutGroups, voiceFamily };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map